import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useTemplates } from "@/hooks/useTemplates";
import { useAdminTemplateTypes, useAdminTemplateFormatTypes } from "@/hooks/useMasterData";
import { projectsApi } from "@/services/projectsApi";
import { Card } from "@/components/ui/card";

interface TemplateSelectionProps {
  projectId: string;
  projectType: string;
  onSelected: () => void;
}

export const TemplateSelection = ({ projectId, projectType, onSelected }: TemplateSelectionProps) => {
  const queryClient = useQueryClient();
  const { data: templates = [] } = useTemplates();
  const { data: templateTypes = [] } = useAdminTemplateTypes();
  const { data: formatTypes = [] } = useAdminTemplateFormatTypes();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const resolvedTypeId = templateTypes.find((t) => t.name.toUpperCase() === projectType.toUpperCase())?.id ?? null;
  const reportingFormatTypeId = formatTypes.find((ft) => ft.name === "Reporting")?.id ?? null;

  const filteredTemplates = templates.filter(
    (t) =>
      t.is_active &&
      (resolvedTypeId == null || t.template_type_id === resolvedTypeId) &&
      (reportingFormatTypeId == null || t.template_formatetype_ids?.includes(reportingFormatTypeId))
  );

  const handleSelect = async (templateId: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      await projectsApi.createProjectTemplate(projectId, templateId, "Reporting");
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "template"] });
      onSelected();
    } catch {
      setSaveError("Failed to save template. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCustom = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await projectsApi.createCustomProjectTemplate(projectId);
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "template"] });
      onSelected();
    } catch {
      setSaveError("Failed to create outline. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold text-foreground">Select Report Outline</h2>
      <p className="text-sm text-muted-foreground mt-1">
        Choose a template structure for your Global Value Dossier
      </p>
      <div className="space-y-3 mt-6">
        {filteredTemplates.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6 border rounded-lg">
            No templates available for this project type.
          </p>
        )}
        {filteredTemplates.map((t) => (
          <button
            key={t.id}
            disabled={saving}
            onClick={() => handleSelect(t.id)}
            className="group w-full text-left border rounded-lg p-5 cursor-pointer hover:border-brand-teal transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-4"
          >
            <div>
              <div className="font-semibold text-foreground">{t.name}</div>
              {t.description && (
                <div className="text-sm text-muted-foreground mt-1">{t.description}</div>
              )}
            </div>
            <span className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity bg-brand-teal text-white text-sm font-medium px-4 py-2">
              Select
            </span>
          </button>
        ))}
        <button
          disabled={saving}
          onClick={handleCustom}
          className="w-full border-2 border-dashed rounded-lg p-5 flex items-center justify-center gap-2 text-sm text-muted-foreground cursor-pointer hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          Create custom Outline
        </button>
      </div>
      {saveError && <p className="text-sm text-destructive mt-3">{saveError}</p>}
    </Card>
  );
};
